import { DoughnutChart } from '@/components/charts/doughnut-chart.tsx'
import type {
  SocialChartData,
  SocialSummarizedData,
} from '@/modules/statistics/social/hooks/use-social-data.ts'
import { useSocialMediaType } from '@/modules/statistics/social/hooks/use-social-media-type.tsx'
import { SidebarChartLegend } from '@/modules/statistics/social/ui/SidebarChartLegend.tsx'
import {
  mapChartDataToFollowersLegendData,
  mapChartDataToViewsLegendData,
} from '@/modules/statistics/social/utils.ts'
import { formatNumberWithPoints } from '@/utils/helpers'

interface SocialAccountSidebarProps {
  chartData: SocialChartData | undefined
  summarizedData: SocialSummarizedData | undefined
}

export const SocialAccountSidebar = ({
  chartData,
  summarizedData,
}: SocialAccountSidebarProps) => {
  const viewsLegendData = mapChartDataToViewsLegendData(chartData)
  const followersLegendData = mapChartDataToFollowersLegendData(chartData)
  const { socialMediaType } = useSocialMediaType()

  return (
    <aside className="flex flex-col">
      <div className="flex flex-col gap-6 xl:flex-row">
        <div className="rounded-lg bg-card p-8 md:min-w-96">
          <div className="text-2xl font-semibold text-title">
            Accounts summarized
          </div>
          <div className="flex flex-col gap-6 sm:flex-row">
            <div className="flex flex-col gap-8 py-4 w-1/2">
              <div className="flex flex-col rounded">
                <span className="text-lg font-semibold text-title">
                  {formatNumberWithPoints(summarizedData?.views)}
                </span>
                <span className="text-sm font-medium text-secondary-300">
                  All Views
                </span>
              </div>
              <div className="flex flex-col rounded">
                <span className="text-lg font-semibold text-title">
                  {formatNumberWithPoints(summarizedData?.followers)}
                </span>
                <span className="text-sm font-medium text-secondary-300">
                  All Followers
                </span>
              </div>
              <div className="flex flex-col rounded">
                <span className="text-lg font-semibold text-title">
                  {formatNumberWithPoints(summarizedData?.totalUploads)}
                </span>
                <span className="text-sm font-medium text-secondary-300">
                  All Uploads
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-8 py-4 w-1/2">
              <div className="flex flex-col rounded">
                <span className="text-lg font-semibold text-title">
                  {formatNumberWithPoints(summarizedData?.likes)}
                </span>
                <span className="text-sm font-medium text-secondary-300">
                  All Likes
                </span>
              </div>

              <div className="flex flex-col rounded">
                <span className="text-lg font-semibold text-title">
                  {formatNumberWithPoints(summarizedData?.shares)}
                </span>
                <span className="text-sm font-medium text-secondary-300">
                  All Shares
                </span>
              </div>

              <div className="flex flex-col rounded">
                <span className="text-lg font-semibold text-title">
                  {formatNumberWithPoints(summarizedData?.comments)}
                </span>
                <span className="text-sm font-medium text-secondary-300">
                  All Comments
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 rounded-lg bg-card p-8 2xl:flex-row">
          <div className="flex flex-col pb-4 pt-8">
            <div className="pb-6 text-xl font-semibold text-text-default">
              All views comparison
            </div>

            <div className="h-full w-full">
              {chartData && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <DoughnutChart
                      data={chartData.views}
                      toggleOptions={{
                        hideLegend: true,
                      }}
                    />
                  </div>
                  <div className="flex h-full items-center">
                    <SidebarChartLegend legendData={viewsLegendData} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col pb-4 pt-8">
            <div className="pb-6 text-xl font-semibold text-text-default">
              All followers comparison
            </div>

            <div className="h-full w-full">
              {chartData && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <DoughnutChart
                      data={chartData.followers}
                      toggleOptions={{
                        hideLegend: true,
                      }}
                    />
                  </div>
                  <div className="flex h-full items-center">
                    <SidebarChartLegend legendData={followersLegendData} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
